import type { Controller } from "../../../services/licensing/client/types.gen";

/**
 * Where a controller sits on the board. `target_version` is what the platform
 * intends a controller to run, `current_version` what it last reported running;
 * they only converge after a deploy.
 */
export type ControllerStatus =
  | "unclaimed"
  | "mismatch"
  | "up_to_date"
  | "unknown";

export const controllerStatusOf = (
  controller: Controller,
): ControllerStatus => {
  if (!controller.site_id) {
    return "unclaimed";
  }
  if (!controller.current_version || !controller.target_version) {
    return "unknown";
  }
  return controller.current_version === controller.target_version
    ? "up_to_date"
    : "mismatch";
};

export type ControllerStatusColumn = {
  status: ControllerStatus;
  title: string;
  hint: string;
  color: string;
};

export const CONTROLLER_STATUS_COLUMNS: ControllerStatusColumn[] = [
  {
    status: "unclaimed",
    title: "Unclaimed",
    hint: "Registered, but not claimed by any site yet. Cannot be deployed to.",
    color: "blue",
  },
  {
    status: "mismatch",
    title: "Version mismatch",
    hint: "Reported a different version than the one it is targeted at — it has not taken its intended version.",
    color: "orange",
  },
  {
    status: "up_to_date",
    title: "Up to date",
    hint: "Reported exactly the version it is targeted at.",
    color: "green",
  },
  {
    status: "unknown",
    title: "Unknown",
    hint: "Claimed, but there is nothing to compare: the controller has never reported a version, or no target version is set.",
    color: "default",
  },
];

export const controllerSortKey = (controller: Controller) =>
  controller.device_info?.serial_numbers?.hostname ?? controller._id ?? "";

export const groupControllersByStatus = (
  controllers: Controller[],
): Record<ControllerStatus, Controller[]> => {
  const grouped: Record<ControllerStatus, Controller[]> = {
    unclaimed: [],
    mismatch: [],
    up_to_date: [],
    unknown: [],
  };

  for (const controller of controllers) {
    grouped[controllerStatusOf(controller)].push(controller);
  }

  for (const status of Object.keys(grouped) as ControllerStatus[]) {
    grouped[status].sort((left, right) =>
      controllerSortKey(left).localeCompare(controllerSortKey(right)),
    );
  }

  return grouped;
};
